import { GoMakeDialog } from "@/components";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LanguageIcon from "@mui/icons-material/Language";
import TuneIcon from "@mui/icons-material/Tune";
import WidgetsOutlinedIcon from "@mui/icons-material/WidgetsOutlined";
import {
  Autocomplete,
  Button,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "he", label: "עברית" },
  { code: "ar", label: "العربية" },
  { code: "de", label: "Deutsch" },
  { code: "tr", label: "Türkçe" },
  { code: "ja", label: "日本語" },
  { code: "pl", label: "Polski" },
  { code: "sq", label: "Shqip" },
  { code: "ru", label: "Русский" },
  { code: "es", label: "Español" },
  { code: "pt", label: "Português" },
  { code: "it", label: "Italiano" },
  { code: "fr", label: "Français" },
  { code: "zh", label: "中文" },
];
const ICONS = [
  "user", "calendar", "truck", "phone", "mail", "pin", "plus", "minus", "x", "trash",
  "save", "send", "printer", "export", "more", "chevron", "search", "file", "note",
  "lock", "coins", "tag", "receipt", "calculator", "pencil", "expand", "layers",
  "square", "clipboard", "briefcase", "arrow-left", "arrow-right",
];

const cardSx = {
  border: "1px solid #E6E8F0",
  borderRadius: "12px",
  background: "#fff",
  p: 2,
  gap: 1.5,
};

const FreeListHint = ({ inline = false }: { inline?: boolean }) => {
  const { t } = useTranslation();
  return (
    <Stack direction="row" alignItems="center" gap={0.5} sx={{ mt: inline ? 0 : 0.5, color: "#5C6BC0" }}>
      <InfoOutlinedIcon sx={{ fontSize: 14 }} />
      <Typography sx={{ fontSize: 12, lineHeight: 1.2, color: "#5C6BC0" }}>
        {t("products.addProduct.admin.freeListValueHint", { defaultValue: "Not listed? Type to add." })}
      </Typography>
    </Stack>
  );
};

const fieldLabel = (text: string, required = false) => (
  <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.5 }}>
    {text}
    {required ? <span style={{ color: "#D32F2F" }}> *</span> : null}
  </Typography>
);

const emptySection = () => ({
  name: "",
  code: "",
  icon: "",
  variableName: "",
  description: "",
  mainSectionId: null as string | null,
  index: "",
  isCanDuplicated: false,
  isCanDeleted: false,
  isShowToClient: true,
  translations: [] as { lang: string; name: string }[],
});

const emptySubSection = () => ({
  name: "",
  code: "",
  type: "",
  variableName: "",
  description: "",
  subSectionDesignType: 1,
  isAccordion: false,
  optionToDuplicateContent: false,
  isShowToClient: true,
  isFinishingSubSection: false,
  translations: [] as { lang: string; name: string }[],
});

const AddTemplateSectionDialog = ({
  open,
  sections,
  initial,
  onClose,
  onSave,
}: {
  open: boolean;
  sections: any[];
  initial?: any;
  onClose: () => void;
  onSave: (section: any) => void;
}) => {
  const { t } = useTranslation();
  const [form, setForm] = useState(emptySection);
  const set = (key: string, value: any) => setForm((current) => ({ ...current, [key]: value }));
  const editing = Boolean(initial?.id);

  useEffect(() => {
    if (!open) return;
    if (!initial) {
      setForm(emptySection());
      return;
    }
    setForm({
      ...emptySection(),
      ...initial,
      index: initial.index ?? "",
      translations: initial.translations || [],
    });
  }, [open, initial]);

  const translations = form.translations.length ? form.translations : [{ lang: "", name: "" }];

  return (
    <GoMakeDialog
      open={open}
      onClose={() => {
        setForm(emptySection());
        onClose();
      }}
      customTitle={
        <Stack direction="row" alignItems="center" gap={1}>
          <WidgetsOutlinedIcon sx={{ color: "#4C45D4" }} />
          <Typography sx={{ fontWeight: 700, fontSize: 18 }}>
            {editing
              ? t("products.addProduct.admin.editSection", { defaultValue: "Edit section" })
              : t("products.addProduct.admin.addSection", { defaultValue: "Add section" })}
          </Typography>
        </Stack>
      }
      onPrimaryAction={() => {
        if (!form.name.trim() || !form.code.trim()) return;
        onSave({
          ...form,
          id: initial?.id,
          parameters: initial?.parameters,
          subSections: initial?.subSections,
          name: form.name.trim(),
          code: form.code.trim(),
          mainSectionId: form.mainSectionId || null,
          index: form.index === "" ? null : Number(form.index),
          translations: translations.filter((item) => item.lang && item.name),
        });
        setForm(emptySection());
      }}
      primaryButtonLabel={editing ? t("common.save") : t("sales.quote.add")}
      cancelButtonLabel={t("modal.cancel")}
      primaryButtonDisabled={!form.name.trim() || !form.code.trim()}
      maxWidth="md"
      fullWidth
    >
      <Stack gap={2}>
        <Typography variant="body2" color="text.secondary">
          {t("products.addProduct.admin.sectionDialogSubtitle", { defaultValue: "Configure section details and visibility" })}
        </Typography>
        <Stack sx={cardSx}>
          <Stack direction="row" alignItems="center" gap={1}>
            <DescriptionOutlinedIcon sx={{ color: "#4C45D4" }} fontSize="small" />
            <Typography sx={{ fontWeight: 700 }}>{t("products.addProduct.admin.basicInformation", { defaultValue: "Basic information" })}</Typography>
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} gap={1.5}>
            <Stack flex={1}>
              {fieldLabel(t("products.addProduct.admin.sectionFieldName", { defaultValue: "Name" }), true)}
              <TextField size="small" placeholder={t("products.addProduct.admin.enterSectionName", { defaultValue: "Enter section name" })} value={form.name} onChange={(e) => set("name", e.target.value)} />
            </Stack>
            <Stack flex={1}>
              {fieldLabel(t("products.addProduct.admin.sectionFieldCode", { defaultValue: "Code" }), true)}
              <TextField size="small" placeholder={t("products.addProduct.admin.enterCode", { defaultValue: "Enter code" })} value={form.code} onChange={(e) => set("code", e.target.value)} />
            </Stack>
            <Stack flex={1}>
              <Stack direction="row" alignItems="baseline" gap={1} sx={{ mb: 0.5 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{t("products.addProduct.admin.sectionIcon", { defaultValue: "Icon" })}</Typography>
                <FreeListHint inline />
              </Stack>
              <Autocomplete
                freeSolo
                size="small"
                options={ICONS}
                value={form.icon || null}
                filterOptions={(options, state) => {
                  const query = state.inputValue.trim().toLowerCase();
                  const filtered = query ? options.filter((option) => option.toLowerCase().includes(query)) : options;
                  const typed = state.inputValue.trim();
                  if (typed && !options.some((option) => option.toLowerCase() === typed.toLowerCase())) {
                    return [...filtered, typed];
                  }
                  return filtered;
                }}
                renderOption={(props, option) => (
                  <li {...props}>
                    {ICONS.includes(option)
                      ? option
                      : t("products.addProduct.admin.addTypedValue", { value: option, defaultValue: "Add \"{{value}}\"" })}
                  </li>
                )}
                onChange={(_event, value) => set("icon", typeof value === "string" ? value.trim() : "")}
                onInputChange={(_event, value, reason) => {
                  if (reason === "input") set("icon", value);
                }}
                renderInput={(params) => (
                  <TextField {...params} placeholder={t("products.addProduct.admin.selectIcon", { defaultValue: "Select icon" })} />
                )}
              />
            </Stack>
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} gap={1.5}>
            <Stack flex={1}>
              {fieldLabel(t("products.addProduct.admin.variableName", { defaultValue: "Variable name" }))}
              <TextField size="small" placeholder={t("products.addProduct.admin.enterVariableName", { defaultValue: "Enter variable name" })} value={form.variableName} onChange={(e) => set("variableName", e.target.value)} />
            </Stack>
            <Stack flex={2}>
              {fieldLabel(t("products.addProduct.admin.details", { defaultValue: "Details" }))}
              <TextField size="small" multiline minRows={2} placeholder={t("products.addProduct.admin.sectionDetailsOptional", { defaultValue: "Enter section details (optional)" })} value={form.description} onChange={(e) => set("description", e.target.value)} />
            </Stack>
          </Stack>
        </Stack>
        <TranslationFields
          items={translations}
          onChange={(next) => set("translations", next)}
        />
        <Stack sx={cardSx}>
          <Stack direction="row" alignItems="center" gap={1}>
            <WidgetsOutlinedIcon sx={{ color: "#4C45D4" }} fontSize="small" />
            <Typography sx={{ fontWeight: 700 }}>{t("products.addProduct.admin.organization", { defaultValue: "Organization" })}</Typography>
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} gap={1.5}>
            <Stack flex={1}>
              {fieldLabel(t("products.addProduct.admin.mainSection", { defaultValue: "Main section" }))}
              <Select size="small" displayEmpty value={form.mainSectionId || ""} onChange={(e) => set("mainSectionId", e.target.value || null)}>
                <MenuItem value="">{t("products.addProduct.admin.selectMainSection", { defaultValue: "Select main section" })}</MenuItem>
                {sections.map((section) => (
                  <MenuItem key={section.id} value={section.id}>{section.name}</MenuItem>
                ))}
              </Select>
            </Stack>
            <Stack sx={{ width: { xs: "100%", sm: 160 } }}>
              {fieldLabel(t("products.addProduct.admin.sectionIndex", { defaultValue: "Index" }))}
              <TextField size="small" type="number" value={form.index} placeholder="1" onChange={(e) => set("index", e.target.value)} />
            </Stack>
          </Stack>
        </Stack>
        <Stack sx={cardSx}>
          <Stack direction="row" alignItems="center" gap={1}>
            <TuneIcon sx={{ color: "#4C45D4" }} fontSize="small" />
            <Typography sx={{ fontWeight: 700 }}>{t("products.addProduct.admin.options", { defaultValue: "Options" })}</Typography>
          </Stack>
          <Stack direction={{ xs: "column", md: "row" }} gap={1.5}>
            <OptionCard
              title={t("products.addProduct.admin.isCanDuplicated", { defaultValue: "Can duplicate" })}
              hint={t("products.addProduct.admin.allowDuplicate", { defaultValue: "Allow this section to be duplicated" })}
              checked={form.isCanDuplicated}
              onChange={(checked) => set("isCanDuplicated", checked)}
            />
            <OptionCard
              title={t("products.addProduct.admin.isCanDeleted", { defaultValue: "Can delete" })}
              hint={t("products.addProduct.admin.allowDelete", { defaultValue: "Allow this section to be deleted" })}
              checked={form.isCanDeleted}
              onChange={(checked) => set("isCanDeleted", checked)}
            />
            <OptionCard
              title={t("products.addProduct.admin.isShowToClient", { defaultValue: "Show to client" })}
              hint={t("products.addProduct.admin.showToClientHint", { defaultValue: "Make this section visible to clients" })}
              checked={form.isShowToClient}
              onChange={(checked) => set("isShowToClient", checked)}
            />
          </Stack>
        </Stack>
      </Stack>
    </GoMakeDialog>
  );
};

const AddTemplateSubSectionDialog = ({
  open,
  initial,
  onClose,
  onSave,
}: {
  open: boolean;
  initial?: any;
  onClose: () => void;
  onSave: (subSection: any) => void;
}) => {
  const { t } = useTranslation();
  const [form, setForm] = useState(emptySubSection);
  const set = (key: string, value: any) => setForm((current) => ({ ...current, [key]: value }));
  const editing = Boolean(initial?.id);

  useEffect(() => {
    if (!open) return;
    if (!initial) {
      setForm(emptySubSection());
      return;
    }
    setForm({
      ...emptySubSection(),
      ...initial,
      translations: initial.translations || [],
    });
  }, [open, initial]);

  return (
    <GoMakeDialog
      open={open}
      onClose={() => {
        setForm(emptySubSection());
        onClose();
      }}
      customTitle={
        <Stack direction="row" alignItems="center" gap={1}>
          <WidgetsOutlinedIcon sx={{ color: "#4C45D4" }} />
          <Typography sx={{ fontWeight: 700, fontSize: 18 }}>
            {editing
              ? t("products.addProduct.admin.editSubSection", { defaultValue: "Edit subsection" })
              : t("products.addProduct.admin.addSubSection", { defaultValue: "Add subsection" })}
          </Typography>
        </Stack>
      }
      onPrimaryAction={() => {
        if (!form.name.trim()) return;
        onSave({
          ...form,
          id: initial?.id,
          parameters: initial?.parameters,
          name: form.name.trim(),
          translations: form.translations.filter((item) => item.lang && item.name),
        });
        setForm(emptySubSection());
      }}
      primaryButtonLabel={editing ? t("common.save") : t("sales.quote.add")}
      cancelButtonLabel={t("modal.cancel")}
      primaryButtonDisabled={!form.name.trim()}
      maxWidth="md"
      fullWidth
    >
      <Stack gap={2}>
        <Stack sx={cardSx}>
          <Stack direction="row" alignItems="center" gap={1}>
            <DescriptionOutlinedIcon sx={{ color: "#4C45D4" }} fontSize="small" />
            <Typography sx={{ fontWeight: 700 }}>{t("products.addProduct.admin.basicInformation", { defaultValue: "Basic information" })}</Typography>
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} gap={1.5}>
            <Stack flex={1}>
              {fieldLabel(t("products.addProduct.admin.sectionFieldName", { defaultValue: "Name" }), true)}
              <TextField size="small" value={form.name} onChange={(e) => set("name", e.target.value)} />
            </Stack>
            <Stack flex={1}>
              {fieldLabel(t("products.addProduct.admin.sectionFieldCode", { defaultValue: "Code" }))}
              <TextField size="small" value={form.code} onChange={(e) => set("code", e.target.value)} />
            </Stack>
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} gap={1.5}>
            <Stack flex={1}>
              {fieldLabel(t("products.addProduct.admin.variableName", { defaultValue: "Variable name" }))}
              <TextField size="small" value={form.variableName} onChange={(e) => set("variableName", e.target.value)} />
            </Stack>
            <Stack flex={1}>
              <Stack direction="row" alignItems="baseline" gap={1} sx={{ mb: 0.5 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{t("products.addProduct.admin.subsectionType", { defaultValue: "Type" })}</Typography>
                <Typography sx={{ fontSize: 12, color: "#5C6BC0" }}>{t("products.addProduct.admin.subsectionTypeHint", { defaultValue: "e.g. Cover, Internal pages" })}</Typography>
              </Stack>
              <TextField size="small" value={form.type} onChange={(e) => set("type", e.target.value)} />
            </Stack>
          </Stack>
          <Stack>
            {fieldLabel(t("products.addProduct.admin.details", { defaultValue: "Details" }))}
            <TextField size="small" multiline minRows={2} value={form.description} onChange={(e) => set("description", e.target.value)} />
          </Stack>
          <Stack>
            {fieldLabel(t("products.addProduct.admin.designType", { defaultValue: "Design type" }))}
            <Select size="small" value={form.subSectionDesignType} onChange={(e) => set("subSectionDesignType", Number(e.target.value))}>
              <MenuItem value={0}>{t("products.addProduct.admin.designWithoutName", { defaultValue: "Without name" })}</MenuItem>
              <MenuItem value={1}>{t("products.addProduct.admin.designStandard", { defaultValue: "Standard" })}</MenuItem>
              <MenuItem value={2}>{t("products.addProduct.admin.designAccordion", { defaultValue: "Accordion" })}</MenuItem>
            </Select>
          </Stack>
        </Stack>
        <TranslationFields items={form.translations.length ? form.translations : [{ lang: "", name: "" }]} onChange={(translations) => set("translations", translations)} />
        <Stack sx={cardSx}>
          <Stack direction="row" alignItems="center" gap={1}>
            <TuneIcon sx={{ color: "#4C45D4" }} fontSize="small" />
            <Typography sx={{ fontWeight: 700 }}>{t("products.addProduct.admin.options", { defaultValue: "Options" })}</Typography>
          </Stack>
          <Stack direction={{ xs: "column", md: "row" }} gap={1.5} flexWrap="wrap">
            <OptionCard title={t("products.addProduct.admin.isAccordion", { defaultValue: "Accordion" })} hint="" checked={form.isAccordion} onChange={(checked) => set("isAccordion", checked)} />
            <OptionCard title={t("products.addProduct.admin.optionToDuplicateContent", { defaultValue: "Duplicate content" })} hint="" checked={form.optionToDuplicateContent} onChange={(checked) => set("optionToDuplicateContent", checked)} />
            <OptionCard title={t("products.addProduct.admin.isShowToClient", { defaultValue: "Show to client" })} hint="" checked={form.isShowToClient} onChange={(checked) => set("isShowToClient", checked)} />
            <OptionCard title={t("products.addProduct.admin.isFinishingSubSection", { defaultValue: "Finishing subsection" })} hint="" checked={form.isFinishingSubSection} onChange={(checked) => set("isFinishingSubSection", checked)} />
          </Stack>
        </Stack>
      </Stack>
    </GoMakeDialog>
  );
};

const OptionCard = ({ title, hint, checked, onChange }: { title: string; hint: string; checked: boolean; onChange: (checked: boolean) => void }) => (
  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ flex: 1, minWidth: 180, border: "1px solid #E6E8F0", borderRadius: "10px", p: 1.5 }}>
    <Stack>
      <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{title}</Typography>
      {hint ? <Typography variant="caption" color="text.secondary">{hint}</Typography> : null}
    </Stack>
    <Switch checked={checked} onChange={(_e, value) => onChange(value)} />
  </Stack>
);

const TranslationFields = ({
  items,
  onChange,
}: {
  items: { lang: string; name: string }[];
  onChange: (items: { lang: string; name: string }[]) => void;
}) => {
  const { t } = useTranslation();
  const update = (index: number, patch: Partial<{ lang: string; name: string }>) => {
    const next = [...items];
    next[index] = { ...items[index], ...patch };
    onChange(next);
  };
  return (
    <Stack gap={1.5} sx={{ ...cardSx, background: "#F7F8FF" }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" alignItems="center" gap={1}>
          <LanguageIcon sx={{ color: "#4C45D4" }} fontSize="small" />
          <Typography sx={{ fontWeight: 700 }}>{t("products.addProduct.admin.translations", { defaultValue: "Translations" })}</Typography>
        </Stack>
        <Button size="small" startIcon={<AddIcon />} onClick={() => onChange([...items, { lang: "", name: "" }])} sx={{ textTransform: "none", color: "#4C45D4 !important" }}>
          {t("products.addProduct.admin.addTranslation", { defaultValue: "Add translation" })}
        </Button>
      </Stack>
      <Stack direction="row" gap={1} sx={{ px: 0.5 }}>
        <Stack direction="row" alignItems="center" gap={1} sx={{ flex: 1 }}>
          <Typography variant="caption">{t("products.addProduct.admin.language", { defaultValue: "Language" })}</Typography>
          <FreeListHint inline />
        </Stack>
        <Typography variant="caption" sx={{ flex: 1 }}>{t("products.addProduct.admin.translationName", { defaultValue: "Translation name" })}</Typography>
        <span style={{ width: 40 }} />
      </Stack>
      {items.map((item, index) => (
        <Stack key={index} direction="row" gap={1} alignItems="flex-start">
          <Stack sx={{ flex: 1 }} gap={0}>
          <Autocomplete
            freeSolo
            size="small"
            options={LANGUAGES}
            sx={{ background: "#fff" }}
            value={LANGUAGES.find((language) => language.code === item.lang) || item.lang || null}
            getOptionLabel={(option) => typeof option === "string" ? option : `${option.label} (${option.code})`}
            isOptionEqualToValue={(option, selected) =>
              typeof option !== "string" && typeof selected !== "string" && option.code === selected.code
            }
            filterOptions={(options, state) => {
              const languages = options.filter((option): option is { code: string; label: string } => typeof option !== "string");
              const query = state.inputValue.trim().toLowerCase();
              const filtered = query
                ? languages.filter((option) => option.code.toLowerCase().includes(query) || option.label.toLowerCase().includes(query))
                : languages;
              const typed = state.inputValue.trim();
              const known = languages.some((option) => option.code.toLowerCase() === typed.toLowerCase() || option.label.toLowerCase() === typed.toLowerCase());
              if (typed && !known) return [...filtered, typed];
              return filtered;
            }}
            renderOption={(props, option) => (
              <li {...props}>
                {typeof option === "string"
                  ? t("products.addProduct.admin.addTypedValue", { value: option, defaultValue: "Add \"{{value}}\"" })
                  : `${option.label} (${option.code})`}
              </li>
            )}
            onChange={(_event, value) => {
              if (!value) {
                update(index, { lang: "" });
                return;
              }
              update(index, { lang: typeof value === "string" ? value.trim() : value.code });
            }}
            onInputChange={(_event, value, reason) => {
              if (reason === "input") update(index, { lang: value });
            }}
            renderInput={(params) => (
              <TextField {...params} placeholder={t("products.addProduct.admin.selectLanguage", { defaultValue: "Select language" })} />
            )}
          />
          </Stack>
          <TextField size="small" placeholder={t("products.addProduct.admin.enterTranslationName", { defaultValue: "Enter translation name" })} value={item.name} onChange={(e) => update(index, { name: e.target.value })} sx={{ flex: 1, background: "#fff" }} />
          <IconButton aria-label={t("materials.buttons.delete")} onClick={() => onChange(items.filter((_, itemIndex) => itemIndex !== index))} sx={{ color: "#D32F2F" }}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Stack>
      ))}
    </Stack>
  );
};

export { AddTemplateSectionDialog, AddTemplateSubSectionDialog };
